import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { SearchField } from '../../../../components/design-system/admin/SearchField';
import { SELECTED_FRAME } from '../../../../components/design-system/common/selection';
import { Thumb } from '../../../../components/design-system/analytics/Thumb';
import { useShopperRequestsApi } from '../../../../common/hooks/use-shopper-requests-api.hook';
import { ShowroomProduct, toShowroomProduct } from './showroom-sheet';

const PAGE_SIZE = 24;

/**
 * Pick the products on display: a searchable grid of the vendor's visible
 * products, several at a time. A product without a SKU cannot be picked —
 * its code would open the visualizer without it.
 */
export function ProductPicker({
    selected,
    onToggle,
    priceText,
}: {
    selected: ShowroomProduct[];
    onToggle: (product: ShowroomProduct) => void;
    priceText: (product: ShowroomProduct) => string | null;
}) {
    const { t } = useTranslation();
    const api = useShopperRequestsApi();
    const [search, setSearch] = useState('');
    const [q, setQ] = useState('');
    const [page, setPage] = useState(1);
    const [items, setItems] = useState<ShowroomProduct[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const latest = useRef(0);

    useEffect(() => {
        const id = setTimeout(() => {
            setQ(search.trim());
            setPage(1);
        }, 300);
        return () => clearTimeout(id);
    }, [search]);

    useEffect(() => {
        const id = ++latest.current;
        setLoading(true);
        setError(false);
        api.listProducts({ search: q, page, limit: PAGE_SIZE })
            .then((r) => {
                if (id !== latest.current) return;
                const rows = r.data.map(toShowroomProduct).filter(Boolean) as ShowroomProduct[];
                setItems((prev) => (page === 1 ? rows : [...prev, ...rows.filter((x) => !prev.some((y) => y.uuid === x.uuid))]));
                setTotal(r.total);
            })
            .catch(() => id === latest.current && setError(true))
            .finally(() => id === latest.current && setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q, page, attempt]);

    const chosen = new Set(selected.map((p) => p.uuid));

    return (
        <div className="flex flex-col gap-3" data-testid="sr-showroom-picker">
            <SearchField value={search} onChange={setSearch} placeholder={t('SHOPPER_REQUESTS.SHOWROOM.SEARCH')} className="sm:w-full" />

            {error && !items.length ? (
                <div className="flex items-center justify-between gap-3 py-4">
                    <p className="text-sm text-muted-foreground">{t('SHOPPER_REQUESTS.SHOWROOM.LOAD_FAILED')}</p>
                    <Button variant="outline" size="sm" onClick={() => setAttempt((n) => n + 1)}>
                        {t('SHOPPER_REQUESTS.RETRY')}
                    </Button>
                </div>
            ) : loading && !items.length ? (
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-3">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <Skeleton key={i} className="aspect-[3/4] rounded-lg" />
                    ))}
                </div>
            ) : items.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">{t('SHOPPER_REQUESTS.SHOWROOM.NO_PRODUCTS')}</p>
            ) : (
                <ul className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-3">
                    {items.map((p) => {
                        const on = chosen.has(p.uuid);
                        const disabled = !p.sku;
                        const price = priceText(p);
                        return (
                            <li key={p.uuid}>
                                <button
                                    type="button"
                                    aria-pressed={on}
                                    disabled={disabled}
                                    title={disabled ? t('SHOPPER_REQUESTS.SHOWROOM.NO_SKU') : p.name}
                                    onClick={() => onToggle(p)}
                                    className={cn(
                                        'relative flex w-full flex-col overflow-hidden rounded-lg border text-start transition-colors',
                                        on ? SELECTED_FRAME : 'border-border hover:border-foreground/25',
                                        disabled && 'cursor-not-allowed opacity-50',
                                    )}
                                    data-testid="sr-showroom-product"
                                >
                                    <Thumb src={p.imageUrl} alt="" size="fill" className="aspect-square w-full rounded-none border-0" />
                                    <span className="flex min-w-0 flex-col gap-0.5 p-2">
                                        <span className="truncate text-xs">{p.name}</span>
                                        <span className="truncate text-[11px] tabular-nums text-muted-foreground">
                                            {disabled ? t('SHOPPER_REQUESTS.SHOWROOM.NO_SKU_SHORT') : price || p.sku}
                                        </span>
                                    </span>
                                    {on && (
                                        <span className="absolute end-1.5 top-1.5 flex size-5 items-center justify-center rounded-full bg-foreground text-background" aria-hidden>
                                            <Check className="size-3" />
                                        </span>
                                    )}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}

            {items.length > 0 && items.length < total && (
                <div className="flex justify-center">
                    <Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)} disabled={loading}>
                        {loading && <Loader2 className="animate-spin" />}
                        {t('SHOPPER_REQUESTS.SHOWROOM.LOAD_MORE')}
                    </Button>
                </div>
            )}
        </div>
    );
}
